// Client-seitige Audio-Normalisierung: beliebiges Audio → MP3 192 kbps, auf
// -14 LUFS / -1 dBTP. 2-Pass: ebur128 misst (I + True Peak), dann Gain + bedingter
// True-Peak-Limiter (siehe loudnessPlan.ts). Spec: docs/truepeak-limiter-2pass.md.
// Single-Thread-Core (kein COOP/COEP/SharedArrayBuffer nötig → keine
// Header-Turnerei in Caddy).
//
// Core wird SELF-HOSTED unter /ffmpeg/ geladen (seit dem Umzug auf Hetzner,
// 2026-09-14): vite.config.ts kopiert ffmpeg-core.js/.wasm aus
// node_modules/@ffmpeg/core beim Build nach dist/ffmpeg/ und liefert sie im
// Dev-Server aus. Auf Cloudflare Pages kam der Core vom jsdelivr-CDN, weil die
// ~30 MiB grosse ffmpeg-core.wasm das 25-MiB-Datei-Limit von Pages sprengte;
// auf Hetzner gibt es das Limit nicht, und die CSP kommt so ohne Fremdhost aus.
// toBlobURL reicht die Dateien als same-origin Blob-URL an den ffmpeg-Worker
// (dessen importScripts sonst zickt).
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { planGain, parseEbur128, buildLoudnessFilter, OUT_SR } from './loudnessPlan';

// ESM-Core (NICHT umd): @ffmpeg/ffmpeg (ESM-Build) erzeugt einen `type:"module"`-Worker,
// der den Core per dynamischem import() lädt — das braucht die ESM-Variante. Der UMD-Core
// (importScripts) funktioniert nur mit dem klassischen Worker des UMD-Builds. Beide ST.
const CORE_BASE = '/ffmpeg';
const CORE_JS = `${CORE_BASE}/ffmpeg-core.js`;
const CORE_WASM = `${CORE_BASE}/ffmpeg-core.wasm`;

export type ConvertPhase = 'load' | 'measure' | 'encode';

let ffPromise: Promise<FFmpeg> | null = null;
// Ein FFmpeg-Isolate mit EINEM WASM-Heap → Läufe müssen serialisiert werden,
// sonst überschreiben sich in-/out-Dateien und der Heap.
let queue: Promise<unknown> = Promise.resolve();

function getFF(): Promise<FFmpeg> {
  if (!ffPromise) {
    ffPromise = (async () => {
      const ff = new FFmpeg();
      // toBlobURL: lädt die (same-origin) Core-Dateien und reicht sie als Blob-URL
      // an den ffmpeg-Worker (dessen importScripts sonst je nach Origin zickt).
      await ff.load({
        coreURL: await toBlobURL(CORE_JS, 'text/javascript'),
        wasmURL: await toBlobURL(CORE_WASM, 'application/wasm'),
      });
      return ff;
    })().catch((e) => { ffPromise = null; throw e; }); // Fehlschlag nicht cachen
  }
  return ffPromise;
}

/**
 * Audio-File → normalisiertes MP3 (192k, -14 LUFS / -1 dBTP) als Blob.
 * Läufe werden global serialisiert (ein WASM-Heap).
 */
export function toNormalizedMp3(file: File, onPhase?: (p: ConvertPhase) => void): Promise<Blob> {
  const run = queue.then(() => convertOne(file, onPhase));
  queue = run.catch(() => {}); // Kette nicht durch einen Fehler brechen
  return run;
}

async function convertOne(file: File, onPhase?: (p: ConvertPhase) => void): Promise<Blob> {
  onPhase?.('load');
  const ff = await getFF();
  const inName = 'in';
  const outName = 'out.mp3';
  let log = '';
  const onLog = ({ message }: { message: string }) => { log += message + '\n'; };
  ff.on('log', onLog);
  try {
    await ff.writeFile(inName, await fetchFile(file));

    // Pass 1: messen → ebur128-Summary (Integrated Loudness + True Peak)
    onPhase?.('measure');
    log = '';
    await ff.exec([
      '-i', inName,
      '-af', 'ebur128=peak=true',
      '-f', 'null', '-',
    ]);
    const m = parseEbur128(log);

    // Pass 2: Gain + bedingter True-Peak-Limiter anwenden, zu MP3 encoden
    onPhase?.('encode');
    const plan = planGain(m.I, m.truePeak);
    await ff.exec([
      '-i', inName,
      '-af', buildLoudnessFilter(plan),
      '-ar', String(OUT_SR), // immer auf OUT_SR encoden (reiner Gain resample't nicht selbst)
      '-c:a', 'libmp3lame', '-b:a', '192k',
      outName,
    ]);

    // readFile gibt eine View auf den WASM-Heap → slice() kopiert raus, bevor der
    // nächste Lauf sie überschreibt.
    const data = (await ff.readFile(outName)) as Uint8Array;
    return new Blob([data.slice()], { type: 'audio/mpeg' });
  } finally {
    ff.off('log', onLog);
    await ff.deleteFile(inName).catch(() => {});
    await ff.deleteFile(outName).catch(() => {});
  }
}
